import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import QuestionCard from "@/components/QuestionCard";
import ReportView from "@/components/ReportView";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link2, Users, LayoutDashboard, PlayCircle, Mail, FileBadge } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";

const NAV_LINKS = [
  { to: "/me", label: "Dashboard", icon: LayoutDashboard, testId: "nav-me-dashboard" },
  { to: "/me/students", label: "My Students", icon: Users, testId: "nav-me-students" },
  { to: "/me/link", label: "My Link", icon: Link2, testId: "nav-me-link" },
  { to: "/me/inbox", label: "Inbox", icon: Mail, testId: "nav-me-inbox" },
  { to: "/me/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-me-sample-report" },
  { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-me-demo" },
];

export default function DemoMode() {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState(0);
  const [index, setIndex] = useState(0);
  const [total, setTotal] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  const links = user?.role === "SUPER_ADMIN" ? [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
    { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
    { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
    { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-admin-demo" },
  ] : NAV_LINKS;

  const launch = async () => {
    const res = await api.post("/demo/start");
    setSessionId(res.data.demo_session_id);
    setReport(null);
    fetchCurrent(res.data.demo_session_id);
  };

  const fetchCurrent = async (sid) => {
    const res = await api.get(`/demo/${sid}/current`);
    if (res.data.completed) {
      try {
        await api.post(`/demo/${sid}/submit`);
      } catch {
        // report generation continues in the background even if this request times out
      }
      setQuestion(null);
      pollForReport(sid);
      return;
    }
    setQuestion(res.data.question);
    setProgress(res.data.progress_percent);
    setIndex(res.data.index);
    setTotal(res.data.total);
    setAnswer(null);
  };

  const pollForReport = async (sid, attempt = 0) => {
    setGenerating(true);
    try {
      const rep = await api.get(`/demo/${sid}/report`);
      if (rep.data.status === "READY") {
        setReport(rep.data);
        setGenerating(false);
        return;
      }
    } catch {
      // not ready yet
    }
    if (attempt > 40) {
      setGenerating(false);
      return;
    }
    setTimeout(() => pollForReport(sid, attempt + 1), 3000);
  };

  const handleNext = async () => {
    if (answer === null) return;
    setSaving(true);
    try {
      await api.post(`/demo/${sessionId}/answer`, { question_id: question.id, answer });
      await fetchCurrent(sessionId);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell links={links} activePath="/me/demo">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="demo-mode-heading">CareerStudy360 Demo Mode</h1>
      <p className="text-sm text-slate-500 mb-8">
        A short, visually engaging sample assessment for presentations. Responses are never stored in production psychometric norms.
      </p>

      {!sessionId && !report && (
        <Button data-testid="launch-demo-button" onClick={launch} className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 h-11">
          Launch Demo
        </Button>
      )}

      {sessionId && question && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-2xl">
          <Progress value={progress} className="h-2 mb-8" data-testid="demo-progress-bar" />
          <QuestionCard question={question} value={answer} onChange={setAnswer} onNext={handleNext} saving={saving} isLast={index === total - 1} />
        </div>
      )}

      {generating && (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-lg text-center" data-testid="demo-report-generating">
          <p className="text-slate-500">Generating your demo report…</p>
        </div>
      )}

      {report && (
        <div className="mt-4">
          <ReportView report={report} isDemo={true} />
          <Button data-testid="run-demo-again-button" onClick={launch} className="mt-8 bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 h-11">
            Run Demo Again
          </Button>
        </div>
      )}
    </DashboardShell>
  );
}
